/**
 * Converts collection page appliedFilters into Algolia's facetFilters format.
 *
 * Format: outer array = AND between groups, inner array = OR within a facet.
 * Matches the search page's refinementList operator: 'or' behavior.
 */

export const FILTER_KEY_TO_ATTRIBUTE = {
  category: 'product_type',
  color: 'meta.custom.color_group',
  size: 'available_sizes',
  type: 'tags',
};

export const NUMERIC_FILTER_KEYS = new Set(['price']);

const CATEGORY_TAG_PREFIX = '[Category: ';
const CATEGORY_TAG_SUFFIX = ']';

/**
 * Builds Algolia facetFilters array from the collection page's appliedFilters object.
 *
 * @param {Object} appliedFilters - { filterKey: [selectedValue, ...] }
 * @param {Object} [dynamicAttributeMap] - Maps dynamic filter keys to Algolia attributes
 *   e.g. { fill: 'named_tags.Fill' }
 * @returns {Array} facetFilters for Algolia API
 *
 * @example
 * buildFacetFilters(
 *   { category: ['Jackets'], color: ['Black', 'Navy'], fill: ['Down'] },
 *   { fill: 'named_tags.Fill' }
 * )
 * // => [
 * //   ["product_type:Jackets"],
 * //   ["meta.custom.color_group:Black", "meta.custom.color_group:Navy"],
 * //   ["named_tags.Fill:Down"],
 * // ]
 */
export function buildFacetFilters(appliedFilters, dynamicAttributeMap = {}) {
  const facetFilters = [];

  for (const [filterKey, values] of Object.entries(appliedFilters)) {
    if (!values || !values.length) continue;
    if (NUMERIC_FILTER_KEYS.has(filterKey)) continue;

    const attribute = dynamicAttributeMap[filterKey] || FILTER_KEY_TO_ATTRIBUTE[filterKey];
    if (!attribute) continue;

    const entries = values.map((value) => {
      if (filterKey === 'type') {
        return `${attribute}:${CATEGORY_TAG_PREFIX}${value}${CATEGORY_TAG_SUFFIX}`;
      }
      return `${attribute}:${value}`;
    });

    facetFilters.push(entries);
  }

  return facetFilters;
}

/**
 * Builds a reverse mapping from dynamic filter keys to Algolia attributes.
 * Used for facets not in the static FILTER_KEY_TO_ATTRIBUTE map.
 *
 * @param {Array<{ attribute: string, filterKey: string }>} orderedFacets
 * @returns {Object} { filterKey: attribute }
 */
export function buildDynamicAttributeMap(orderedFacets) {
  const map = {};

  for (const facet of orderedFacets) {
    if (NUMERIC_FILTER_KEYS.has(facet.filterKey)) continue;

    const staticAttribute = FILTER_KEY_TO_ATTRIBUTE[facet.filterKey];
    if (!staticAttribute || staticAttribute !== facet.attribute) {
      map[facet.filterKey] = facet.attribute;
    }
  }

  return map;
}
